/**
 * An engraving, coloured to the theme (DESIGN_SYSTEM §8, "Imagery").
 *
 * The file is greyscale — black ink on white paper. Two blend layers turn it
 * into the theme's two colours, so one file serves both palettes:
 *
 * - **Tide** (paper lighter than ink): the image multiplies onto a ground of
 *   `--bg`, so white becomes the ground and black stays black; a layer of
 *   `--tx` *lightens* over it, so black becomes the ink and the ground, being
 *   lighter, stays.
 * - **Abyss** (paper darker than ink): the image is inverted and *screened*
 *   onto `--bg`, so the lines glow; a layer of `--acc` *darkens* over it, so
 *   they glow in the accent rather than white.
 *
 * The rules are in `index.css` (`.engraving`), keyed on `data-theme`, because
 * the blend has to flip with the ground's lightness — the colours themselves
 * are only ever the palette's tokens (D-2). Decorative: `alt=""` and hidden
 * from assistive technology (D-4's rule — nothing is said only by a picture).
 */

import { type EngravingName, ENGRAVINGS, engravingSrc } from "@/features/common/engravings";
import { cx } from "@/features/common/primitives";

export function Engraving({
  name,
  fade = false,
  className,
  style,
}: {
  name: EngravingName;
  /** Fade the lower part into the ground, for an image that sits behind text. */
  fade?: boolean;
  className?: string;
  /** Sizing only — the duotone is the stylesheet's (`.engraving`). */
  style?: React.CSSProperties;
}): React.JSX.Element {
  const { width, height } = ENGRAVINGS[name];
  return (
    <div aria-hidden="true" data-fade={fade || undefined} className={cx("engraving", className)} style={style}>
      <img src={engravingSrc(name)} alt="" width={width} height={height} loading="lazy" decoding="async" />
    </div>
  );
}
